// Mock data for the v3 teacher dashboard (ported from the Cloud Design wireframe).
// All generation is seeded/deterministic so the Roster, Intervene, and Gradebook
// pages agree on the same students. Swap this module for real API data later.

export const CLASSES = [
  // p1 sits in its final week so the unlocked Finish Unit & Sync state is demoable
  { id: 'p1', period: 'Period 1', name: 'Algebra II',   size: 24, unit: { subject: 'math',    domain: 'algebra', targetBand: 4, week: 4, total: 4 } },
  { id: 'p3', period: 'Period 3', name: 'Algebra II',   size: 27, unit: { subject: 'math',    domain: 'algebra', targetBand: 4, week: 2, total: 4 } },
  { id: 'p5', period: 'Period 5', name: 'Geometry H',   size: 22, unit: { subject: 'math',    domain: 'geotrig', targetBand: 5, week: 1, total: 3 } },
  { id: 'p6', period: 'Period 6', name: 'SAT Bootcamp', size: 29, unit: { subject: 'reading', domain: 'info',    targetBand: 6, week: 3, total: 5 } },
];

export const DOMAINS = {
  math: [
    { key: 'algebra',  short: 'Algebra',  full: 'Algebra' },
    { key: 'psda',     short: 'PSDA',     full: 'Problem Solving & Data Analysis' },
    { key: 'advanced', short: 'Advanced', full: 'Advanced Math' },
    { key: 'geotrig',  short: 'Geo/Trig', full: 'Geometry & Trigonometry' },
  ],
  reading: [
    { key: 'info',        short: 'Info & Ideas',    full: 'Information & Ideas' },
    { key: 'craft',       short: 'Craft & Struct.', full: 'Craft & Structure' },
    { key: 'conventions', short: 'Conventions',     full: 'Standard English Conventions' },
    { key: 'expression',  short: 'Expr. of Ideas',  full: 'Expression of Ideas' },
  ],
};

export const CLASS_COLORS = { p1: '#3b82f6', p3: '#8F00FF', p5: '#16a34a', p6: '#f59e0b' };

export const domainOf = (subject, key) => DOMAINS[subject].find((d) => d.key === key);
export const subjectLabel = (subject) => (subject === 'math' ? 'Math' : 'Reading & Writing');
export const qualityTarget = (subject) => (subject === 'math' ? 45 : 40);
export const expectedPctOf = (unit) => Math.round((unit.week / unit.total) * 100);

const HOW_UNITS =
  'The domain is split into seven bands, easiest to hardest. Each band is a short unit with its own pacing window. Students practice adaptively and advance a band once they hit mastery.';

// Teacher-facing intro for each domain: shown in the "Domain guide" slide-over.
export const DOMAIN_GUIDES = {
  algebra: {
    name: 'Algebra',
    whatIs: 'The linear domain: linear equations and inequalities in one and two variables, systems of linear equations, and linear functions. About 35% of SAT math, tied with Advanced Math for the largest slice.',
    howUnits: HOW_UNITS,
    skills: ['Linear equations in one & two variables', 'Systems of linear equations', 'Linear functions & their graphs', 'Linear inequalities & absolute value', 'Interpreting slope & intercepts in context'],
  },
  psda: {
    name: 'Problem-Solving & Data Analysis',
    whatIs: 'The real-world math domain: ratios and proportions, percentages, unit conversion, tables and scatterplots, probability, and statistics. About 15% of SAT math, and the only domain built around data.',
    howUnits: HOW_UNITS,
    skills: ['Ratios, rates & proportions', 'Percentages & percent change', 'Tables, scatterplots & lines of best fit', 'Probability & two-way tables', 'Statistics: center, spread & margin of error'],
  },
  advanced: {
    name: 'Advanced Math',
    whatIs: 'The nonlinear domain: quadratics, exponentials, polynomials, radicals and rationals, and function notation. About 35% of SAT math and the biggest lever for high scorers.',
    howUnits: HOW_UNITS,
    skills: ['Quadratic equations & parabolas', 'Exponential growth & decay', 'Polynomials, radicals & rational expressions', 'Function notation & transformations', 'Nonlinear systems'],
  },
  geotrig: {
    name: 'Geometry & Trigonometry',
    whatIs: 'Shapes, angles, and measurement: lines and triangles, area and volume, circles, and right-triangle trig. About 15% of SAT math, but reliably tested on every form.',
    howUnits: HOW_UNITS,
    skills: ['Lines, angles & triangles', 'Area, perimeter & volume', 'Similarity & the Pythagorean theorem', 'Circles: arcs, sectors & equations', 'Right-triangle trig & radians'],
  },
  info: {
    name: 'Information & Ideas',
    whatIs: 'The comprehension domain: finding central ideas and details, using textual and quantitative evidence, and drawing sound inferences from what a passage actually says.',
    howUnits: HOW_UNITS,
    skills: ['Central ideas & details', 'Command of textual evidence', 'Reading tables & graphs as evidence', 'Drawing inferences', 'Evaluating claims'],
  },
  craft: {
    name: 'Craft & Structure',
    whatIs: 'The close-reading domain: words in context, how a text is structured, what a sentence or passage is doing, and connecting ideas across paired texts.',
    howUnits: HOW_UNITS,
    skills: ['Words in context', 'Structure of a passage', 'Function of a sentence', 'Purpose of a passage', 'Cross-text connections'],
  },
  conventions: {
    name: 'Standard English Conventions',
    whatIs: 'The grammar domain: sentence boundaries, punctuation, verb agreement, pronouns, and modifiers. Pure rules, tested the same way every time.',
    howUnits: HOW_UNITS,
    skills: ['Sentence boundaries', 'Punctuation', 'Subject-verb agreement', 'Pronouns & modifiers', 'Verb tense & voice'],
  },
  expression: {
    name: 'Expression of Ideas',
    whatIs: 'The revision domain: choosing the transition that fits the logic of a passage, and synthesizing notes to accomplish a specific rhetorical goal.',
    howUnits: HOW_UNITS,
    skills: ['Transitions: continue, contrast, cause', 'Rhetorical synthesis', 'Matching a writer’s goal', 'Combining ideas precisely', 'Logical flow between sentences'],
  },
};

// The subskill a student is most likely blocked on, by domain and band.
const BAND_SUBSKILLS = {
  algebra: {
    1: ['isolating the variable', 'combining like terms'],
    2: ['two-step equations', 'distributing across parentheses'],
    3: ['slope-intercept form', 'slope from two points'],
    4: ['solving systems', 'substitution & elimination'],
    5: ['linear inequalities', 'absolute value equations'],
    6: ['translating words into equations', 'interpreting slope in context'],
    7: ['systems with no or infinite solutions', 'solving for a parameter'],
    8: ['linear inequalities in two variables', 'systems of inequalities'],
    9: ['parallel & perpendicular line equations', 'equivalent linear forms'],
    10: ['linear modeling with constraints', 'hardest multi-step word problems'],
  },
  geotrig: {
    1: ['angle relationships', 'perimeter & area basics'],
    2: ['triangle angle sums', 'parallel lines & transversals'],
    3: ['the Pythagorean theorem', 'volume of prisms & cylinders'],
    4: ['similar triangles', 'scale factors'],
    5: ['right-triangle trig ratios', 'special right triangles'],
    6: ['arc length & sector area', 'equation of a circle'],
    7: ['completing the square for circles', 'trig of complementary angles'],
    8: ['degrees & radians', 'volume of spheres & cones'],
    9: ['density & composite solids', 'inscribed angles'],
    10: ['multi-step composite figures', 'trig in context'],
  },
  info: {
    1: ['finding stated details', 'main idea of a paragraph'],
    2: ['central ideas & themes', 'summarizing a passage'],
    3: ['locating textual evidence', 'paraphrase vs. quotation'],
    4: ['supporting a claim with evidence', 'reading data in tables'],
    5: ['drawing basic inferences', 'connecting claims to data'],
    6: ['evaluating strength of evidence', 'inferences across paired texts'],
    7: ['quantitative evidence in graphs', 'implicit central ideas'],
    8: ['synthesizing across texts', 'evaluating competing claims'],
    9: ['multi-step inference chains', 'strengthening & undermining claims'],
    10: ['dense historical passages', 'subtle evidence traps'],
  },
};

export function subskillFor(domainKey, name, band) {
  const pool = (BAND_SUBSKILLS[domainKey] || {})[band] || ['core skills for this band'];
  return pool[hash(name) % pool.length];
}

// Recommended next unit once the current one wraps: next domain in the subject's list.
export function nextDomainOf(cls) {
  const list = DOMAINS[cls.unit.subject];
  const idx = list.findIndex((d) => d.key === cls.unit.domain);
  const next = list[(idx + 1) % list.length];
  const guide = DOMAIN_GUIDES[next.key];
  return {
    ...next,
    guide,
    weeks: 4,
    rationale:
      'The class is strong in ' + domainOf(cls.unit.subject, cls.unit.domain).full +
      '. ' + guide.name + ' is the next biggest score lever for this group.',
  };
}

const FIRST_NAMES = ['Aaliyah','Marcus','Sofia','Devin','Priya','Tyler','Hannah','Jamal','Emily','Noah','Isabella','Ethan','Maya','Lucas','Grace','Diego','Olivia','Caleb','Zoe','Aiden','Layla','Mateo','Chloe','Omar','Nina','Wyatt','Aisha','Liam','Camila','Jonah','Ruby','Andre','Talia','Felix','Yuki','Darius','Elena','Cole','Sana','Brody'];
const LAST_NAMES = ['Brooks','Lee','Ramirez','Carter','Nair','Nguyen','Goldberg','Washington','Chen','Patterson','Rossi','Johnson','Müller','Kim','Alvarez','Bennett','Foster','Okafor','Park','Santos','Reyes','Cohen','Hassan','Walsh','Petrov','Ito','Diallo','Mensah','Cruz','Novak','Singh','Tran','Boyd','Flores','Haddad','Wong','Abebe','Lindqvist','Romano','Khan'];

export const lastNameOf = (name) => name.trim().split(/\s+/).slice(-1)[0];

// Roster order (DESIGN.md: names show "First Last" and sort by last name),
// with natural numbers so "Student 2" comes before "Student 10".
const NAME_ORDER = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
export const compareByLastName = (a, b) => {
  const x = String(a || ''); const y = String(b || '');
  return NAME_ORDER.compare(lastNameOf(x), lastNameOf(y)) || NAME_ORDER.compare(x, y);
};

export function initials(name) {
  return name.split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase();
}

// FNV-1a: stable per-student values (gradebook minutes, subskill picks).
export function hash(str) {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

// Mulberry32-style seeded RNG.
function rand(seed) {
  let t = seed >>> 0;
  return () => {
    t = (t + 0x6d2b79f5) >>> 0;
    let x = Math.imul(t ^ (t >>> 15), 1 | t);
    x ^= x + Math.imul(x ^ (x >>> 7), 61 | x);
    return ((x ^ (x >>> 14)) >>> 0) / 4294967296;
  };
}

let rosterCache = null;

export function getRosters() {
  if (rosterCache) return rosterCache;
  const rosters = {};
  let seed = 91;
  for (const c of CLASSES) {
    const r = rand(seed++ * 2654435761);
    const pick = (arr) => arr[Math.floor(r() * arr.length)];
    const band = () => 1 + Math.floor(r() * 7);
    const expected = (c.unit.week / c.unit.total) * 100;
    const target = qualityTarget(c.unit.subject);
    const used = new Set();
    const list = [];
    for (let i = 0; i < c.size; i++) {
      let name;
      do { name = pick(FIRST_NAMES) + ' ' + pick(LAST_NAMES); } while (used.has(name));
      used.add(name);
      let completion = Math.round((expected + (r() - 0.5) * 120) / 5) * 5;
      completion = Math.max(0, Math.min(100, completion));
      const stuck = r() < 0.13;
      const paceDelta = completion - expected;
      let qualMin;
      if (stuck) {
        // grinding the same content but blocked: high minutes, low progress
        qualMin = Math.min(72, target + 6 + Math.floor(r() * 22));
      } else {
        // minutes track pace: ahead → above target, behind → below
        qualMin = Math.round(target + paceDelta * 0.5 + (r() - 0.5) * 14);
        qualMin = Math.max(0, Math.min(72, qualMin));
      }
      list.push({
        name,
        math:    { algebra: band(), psda: band(), advanced: band(), geotrig: band() },
        reading: { info: band(), craft: band(), conventions: band(), expression: band() },
        completion,
        qualMin,
        stuck,
      });
    }
    rosters[c.id] = list;
  }
  rosterCache = rosters;
  return rosters;
}

export const getRoster = (classId) => getRosters()[classId] || [];

// Why a student is off track: 'stuck' (blocked despite effort), 'low' (not enough minutes), or 'none'.
export function flagOf(stu, unit) {
  if (stu.stuck) return 'stuck';
  const expected = (unit.week / unit.total) * 100;
  if (stu.completion - expected <= -10 && stu.qualMin < qualityTarget(unit.subject)) return 'low';
  return 'none';
}

// Why a 'low' student's practice isn't landing (Sandy 2026-07-11), three buckets:
// 'minutes'  - not logging practice time at all
// 'cheating' - progress doesn't match the work (too fast, suddenly perfect)
// 'clicking' - logs the minutes but rushes through without trying
export const LOW_REASON_LABEL = {
  minutes: 'Missing minutes',
  cheating: 'Suspected cheating',
  clicking: 'Clicking through',
};
export function lowReasonOf(stu) {
  // salt 'r3' chosen so the default class (p3) shows all three groups
  const n = hash(stu.name + 'r3') % 10;
  if (n === 0) return 'cheating';
  if (n <= 2) return 'clicking';
  return 'minutes';
}

// Two pace states only (Sandy 2026-07-07): ahead of the window is just "On pace".
// Red is reserved for the Stuck flag, so Behind stays amber at any severity.
export function paceBucket(delta) {
  if (delta <= -10) return { key: 'behind', label: 'Behind', bar: '#f59e0b' };
  return { key: 'on', label: 'On pace', bar: '#22c55e' };
}

// ---- Gradebook (end-of-unit unit grades; no weekly grades, Sandy 2026-07-11) ----

export const LMS = 'PowerSchool'; // set to 'None' to show the CSV export path

// Unit grade = % of the band completed × a loss multiplier if the work was
// suspected cheating or low-effort clicking, + optional extra credit for
// finishing the band and continuing past it.
export const GRADE_LOSS = { cheating: 0.5, clicking: 0.75 };

export function unitGradeOf(stu, unit, { extraCredit = false, loss = GRADE_LOSS } = {}) {
  const base = stu.completion;
  const flag = flagOf(stu, unit);
  const reason = flag === 'low' ? lowReasonOf(stu) : null;
  const mult = reason && loss[reason] ? loss[reason] : 1;
  // Deterministic bonus for students who cleared their band and kept going.
  const extra = extraCredit && stu.completion >= 100 ? 5 + (hash(stu.name + 'xc') % 21) : 0;
  return { base, reason, mult, extra, final: Math.round(base * mult) + extra };
}

// Unit history for a class. Unit 1 is the most recent (the one in progress)
// and sits farthest left; higher numbers are older units.
export function unitsOf(cls) {
  const past = DOMAINS[cls.unit.subject]
    .map((d) => d.key)
    .filter((k) => k !== cls.unit.domain)
    .slice(0, 2);
  return [
    { n: 1, domain: cls.unit.domain, current: true },
    ...past.map((domain, i) => ({ n: i + 2, domain, current: false })),
  ];
}

// ---- Live session (the monitoring view) ----
// What each student is doing RIGHT NOW in the active session. Mock: derived
// deterministically from the same flags the summary box uses so the numbers
// agree. States: working · stuck · cheating · clicking · idle (no progress).
export const SESSION_STATE = {
  working:    { label: 'Working',            dot: '#22c55e' },
  stuck:      { label: 'Stuck',              dot: '#dc2626' },
  cheating:   { label: 'Suspected cheating', dot: '#9333ea' },
  clicking:   { label: 'Clicking through',   dot: '#f59e0b' },
  // Split (Sandy 2026-08-09): tabbing = >3 tab-aways; idle = no focused time
  // in 2 min; noprogress = active but nothing moved in 3 min; notstarted =
  // zero session activity (true from second zero — a fresh session shows the
  // whole class here, not as "Working").
  tabbing:    { label: 'Tabbing away',       dot: '#0ea5e9' },
  idle:       { label: 'Idle',               dot: '#a3a3a3' },
  noprogress: { label: 'No progress',        dot: '#737373' },
  notstarted: { label: 'Not started',        dot: '#d4d4d4' },
};

export function sessionOf(stu, unit) {
  const h = hash(stu.name + 'live');
  const fl = flagOf(stu, unit);
  const reason = fl === 'low' ? lowReasonOf(stu) : null;
  const state =
    fl === 'stuck' ? 'stuck'
      : reason === 'cheating' ? 'cheating'
        : reason === 'clicking' ? 'clicking'
          : reason === 'minutes' ? 'idle'
            : 'working';
  const band = stu[unit.subject][unit.domain];
  const skill = subskillFor(unit.domain, stu.name, band);
  const activity =
    state === 'stuck'
      ? (h % 3 === 0 ? `Practice (relearn) · ${skill}` : 'Skill check')
      : [`Lesson · ${skill}`, `Practice · ${skill}`, 'Warm-up', 'Mixed practice'][h % 4];
  const partsDone =
    state === 'working' ? 1 + (h % 3)
      : state === 'cheating' ? 2 + (h % 3) // progress that doesn't match the minutes
        : state === 'clicking' ? 1 + (h % 2)
          : 0;
  const minutes =
    state === 'idle' ? (h % 2 ? 0 : 2 + (h % 5))
      : state === 'cheating' ? 3 + (h % 5) // too fast for the work shown
        : state === 'stuck' ? 24 + (h % 16)
          : 12 + (h % 18);
  return { state, activity, partsDone, minutes };
}

// ---- Stuck detail (Sandy 2026-07-14): stuck is limited to a failed skill
// check or practice-after-relearn that still isn't landing. The teacher gets
// the concept, a one-line brief, the exact question missed, and the
// reflections the student typed in the miss walkthrough. ----
const STUCK_QUESTIONS = {
  algebra: {
    prompt: 'If 3(x − 2) = 12, what is the value of x?',
    choices: ['2', '4', '6', '18'],
    correct: 2, picked: 1,
    reflections: [
      '"I divided both sides by 3 and got x − 2 = 4, then I wrote 4."',
      '"I forgot I still had to add the 2 back."',
    ],
  },
  geotrig: {
    prompt: 'A right triangle has legs of length 6 and 8. What is the length of the hypotenuse?',
    choices: ['10', '12', '14', '48'],
    correct: 0, picked: 2,
    reflections: [
      '"I added 6 and 8."',
      '"I know there\'s a formula but I wasn\'t sure when it applies."',
    ],
  },
  info: {
    prompt: 'The author claims community gardens lower neighborhood food costs. Which finding, if true, would best support the claim?',
    choices: [
      'Garden plots are assigned by lottery each spring.',
      'Families with plots spend 22% less on produce than comparable families without.',
      'Most gardeners describe the garden as a social hub.',
      'The city plans to double the number of plots next year.',
    ],
    correct: 1, picked: 2,
    reflections: [
      '"Mine was true, so it felt right."',
      '"I didn\'t check whether it was about costs."',
    ],
  },
  default: {
    prompt: 'Which choice best completes the text so it follows the logic of the passage?',
    choices: ['however', 'therefore', 'for example', 'similarly'],
    correct: 1, picked: 0,
    reflections: [
      '"The two sentences felt different so I picked a contrast word."',
      '"I didn\'t reread the first sentence before choosing."',
    ],
  },
};

export function stuckDetailOf(stu, unit) {
  const band = stu[unit.subject][unit.domain];
  const subskill = subskillFor(unit.domain, stu.name, band);
  const q = STUCK_QUESTIONS[unit.domain] || STUCK_QUESTIONS.default;
  const atCheck = hash(stu.name + 'wh') % 3 !== 0;
  return {
    band,
    subskill,
    whereLabel: atCheck
      ? 'Failed the Band ' + band + ' skill check'
      : 'Relearned, and practice still isn\'t landing',
    brief:
      'Blocked on ' + subskill + '. They\'re putting the time in; the miss pattern below shows where the ' +
      'solve breaks. A 2-minute walkthrough of one example usually unblocks this.',
    question: q,
  };
}

// Grade for a student in a given unit column. The current unit grades from
// live roster state; past units are deterministic finished history.
export function unitGradeFor(stu, cls, u, opts = {}) {
  if (u.current) return unitGradeOf(stu, cls.unit, opts);
  const { extraCredit = false, loss = GRADE_LOSS } = opts;
  const h = hash(stu.name + 'u' + u.n + u.domain);
  const base = Math.min(100, 40 + (h % 63));
  const rn = h % 20;
  const reason = base < 100 ? (rn === 0 ? 'cheating' : rn <= 2 ? 'clicking' : null) : null;
  const mult = reason && loss[reason] ? loss[reason] : 1;
  const extra = extraCredit && base >= 100 ? 4 + (h % 18) : 0;
  return { base, reason, mult, extra, final: Math.round(base * mult) + extra };
}
